import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, type Track } from '../api.js';
import ApprovalCard from '../components/ApprovalCard.js';
import ConfirmDialog from '../components/ConfirmDialog.js';
import EventTimeline from '../components/EventTimeline.js';
import RepoBadge from '../components/RepoBadge.js';

interface TrackWithReview extends Track {
  review_verdict?: 'approve' | 'flag' | 'block' | null;
  review_summary?: string | null;
  ciso_verdict?: 'approve' | 'flag' | 'block' | null;
  ciso_summary?: string | null;
  review_round_number?: number;
  review_still_open_count?: number;
  review_resolution_kind?: string | null;
  ciso_round_number?: number;
  ciso_still_open_count?: number;
  ciso_resolution_kind?: string | null;
  rebuilt_from_track_id?: string | null;
  rebuild_generation?: number;
}

function RoundStatusLine({
  roundNumber,
  stillOpenCount,
  resolutionKind,
}: {
  roundNumber?: number;
  stillOpenCount?: number;
  resolutionKind?: string | null;
}) {
  if (!roundNumber) return null;
  return (
    <div className="card-sub">
      round {roundNumber} · still open: {stillOpenCount ?? 0}
      {resolutionKind === 'approved_by_rebuttal' && (
        <span className="badge badge-yellow" style={{ marginLeft: 6 }}>
          approved by rebuttal — no code change
        </span>
      )}
    </div>
  );
}

function verdictBadgeClass(verdict: string): string {
  if (verdict === 'approve') return 'badge-green';
  if (verdict === 'flag') return 'badge-yellow';
  return 'badge-red';
}

type SmokeGateState = 'passing' | 'failing' | 'running' | 'none';

interface SmokeGate {
  state: SmokeGateState;
  detail?: string;
  url?: string;
}

function smokeChipClass(state: SmokeGateState): string {
  if (state === 'passing') return 'badge-green';
  if (state === 'failing') return 'badge-red';
  if (state === 'running') return 'badge-yellow';
  return 'badge-gray';
}

async function fetchMergeGate(id: string): Promise<SmokeGate> {
  const res = await fetch(`/api/tracks/${id}/merge-gate`);
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok || !body) return { state: 'none' };
  return body as SmokeGate;
}

interface CiIndeterminateInfo {
  detail: string;
  ci_lookup_status: number | null;
  ci_lookup_message: string | null;
}

async function mergeTrack(id: string, acknowledgeUnverifiedCi?: boolean): Promise<void> {
  const res = await fetch(`/api/tracks/${id}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(acknowledgeUnverifiedCi ? { acknowledge_unverified_ci: true } : {}),
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? body?.detail ?? `request failed: ${res.status}`,
      body?.blocked,
      body
    );
  }
}

export default function TrackDetail() {
  const { id } = useParams<{ id: string }>();
  const [track, setTrack] = useState<Track | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mergeConfirmed, setMergeConfirmed] = useState(false);
  const [smokeGate, setSmokeGate] = useState<SmokeGate | null>(null);
  const [ciConfirm, setCiConfirm] = useState<CiIndeterminateInfo | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.getTrack(id);
      setTrack(r.track);
      setError(null);
      if (r.track.state === 'PR_OPEN') {
        setSmokeGate(await fetchMergeGate(id));
      } else {
        setSmokeGate(null);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  async function runAction(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!id) return <div className="errors">missing track id</div>;
  if (error && !track) return <div className="errors">{error}</div>;
  if (!track) return <div className="empty">Loading…</div>;

  const reviewedTrack = track as TrackWithReview;
  const hasPrereqs = (track.spec.prereqs ?? []).length > 0;
  const actions: React.ReactNode[] = [];

  if (track.state === 'SPEC_REVIEW') {
    actions.push(
      <button key="approve" className="btn-conductor" disabled={busy}
        onClick={() => runAction(() => api.approve(track.id))}>
        {hasPrereqs ? 'Approve — needs prereqs first' : 'Approve & build'}
      </button>,
      <button key="revise" className="btn-outline-conductor" disabled={busy}
        onClick={() => runAction(() => api.revise(track.id))}>
        Revise
      </button>,
      <button key="cancel" className="btn-outline-conductor drawer-btn-danger" disabled={busy}
        onClick={() => runAction(() => api.cancel(track.id))}>
        Cancel
      </button>
    );
  } else if (track.state === 'AWAITING_PREREQS') {
    actions.push(
      <button key="prereqs" className="btn-conductor" disabled={busy}
        onClick={() => runAction(() => api.prereqsDone(track.id))}>
        I&apos;ve done the prereqs
      </button>,
      <button key="cancel" className="btn-outline-conductor drawer-btn-danger" disabled={busy}
        onClick={() => runAction(() => api.cancel(track.id))}>
        Cancel
      </button>
    );
  } else if (track.state === 'PR_OPEN') {
    if (track.pr_url) {
      actions.push(
        <a key="pr" className="btn-outline-conductor" href={track.pr_url} target="_blank" rel="noreferrer">
          View PR #{track.pr_number ?? ''}
        </a>
      );
    }
    actions.push(
      <span key="merge-repo" style={{ display: 'inline-flex', alignItems: 'center' }}>
        <RepoBadge targetRepo={track.spec.target_repo} size="large" />
      </span>
    );
    if (smokeGate) {
      actions.push(
        smokeGate.url ? (
          <a key="tests" className={`badge ${smokeChipClass(smokeGate.state)}`} href={smokeGate.url}
            target="_blank" rel="noreferrer" title={smokeGate.detail ?? ''}>
            tests: {smokeGate.state}
          </a>
        ) : (
          <span key="tests" className={`badge ${smokeChipClass(smokeGate.state)}`} title={smokeGate.detail ?? ''}>
            tests: {smokeGate.state}
          </span>
        )
      );
    }
    actions.push(
      <button key="merge" className="btn-conductor" disabled={busy}
        onClick={() => runAction(async () => {
          try {
            await mergeTrack(track.id);
            setMergeConfirmed(true);
          } catch (e) {
            if (e instanceof ApiError && e.code === 'ci_status_indeterminate') {
              const details = (e.details ?? {}) as { ci_lookup_status?: number | null; ci_lookup_message?: string | null };
              setCiConfirm({
                detail: e.message,
                ci_lookup_status: details.ci_lookup_status ?? null,
                ci_lookup_message: details.ci_lookup_message ?? null,
              });
              return;
            }
            throw e;
          }
        })}>
        Approve & Merge
      </button>,
      <button key="cancel" className="btn-outline-conductor drawer-btn-danger" disabled={busy}
        onClick={() => runAction(() => api.cancel(track.id))}>
        Cancel
      </button>
    );
  } else if (track.state !== 'DONE' && track.state !== 'CANCELLED' && track.state !== 'MERGED') {
    actions.push(
      <button key="cancel" className="btn-outline-conductor drawer-btn-danger" disabled={busy}
        onClick={() => runAction(() => api.cancel(track.id))}>
        Cancel
      </button>
    );
  }

  return (
    <div>
      {error && <div className="errors">{error}</div>}
      {mergeConfirmed && <div className="empty">Merge requested — waiting for GitHub to confirm.</div>}
      {ciConfirm && (
        <ConfirmDialog
          title="CI status could not be verified"
          message={
            <>
              <p>{ciConfirm.detail}</p>
              {ciConfirm.ci_lookup_status !== null && (
                <p>
                  GitHub returned status {ciConfirm.ci_lookup_status}
                  {ciConfirm.ci_lookup_message ? `: ${ciConfirm.ci_lookup_message}` : ''}.
                </p>
              )}
              <p>Proceeding merges this PR without automated CI verification.</p>
            </>
          }
          confirmLabel="Merge without verified CI"
          danger
          busy={busy}
          onCancel={() => setCiConfirm(null)}
          onConfirm={() =>
            runAction(async () => {
              setCiConfirm(null);
              await mergeTrack(track.id, true);
              setMergeConfirmed(true);
            })
          }
        />
      )}
      <ApprovalCard track={track} />
      {reviewedTrack.session_url && (
        <div className="badge-row">
          <a className="badge badge-gray" href={reviewedTrack.session_url} target="_blank" rel="noreferrer">
            Open session
          </a>
        </div>
      )}
      {actions.length > 0 && <div className="actions">{actions}</div>}
      {reviewedTrack.review_verdict && (
        <div className="section">
          <h3>CTO Review</h3>
          <div className="badge-row">
            <span className={`badge ${verdictBadgeClass(reviewedTrack.review_verdict)}`}>
              {reviewedTrack.review_verdict}
            </span>
          </div>
          {reviewedTrack.review_summary && (
            <div className="one-line">{reviewedTrack.review_summary}</div>
          )}
          <RoundStatusLine
            roundNumber={reviewedTrack.review_round_number}
            stillOpenCount={reviewedTrack.review_still_open_count}
            resolutionKind={reviewedTrack.review_resolution_kind}
          />
        </div>
      )}
      {reviewedTrack.ciso_verdict && (
        <div className="section">
          <h3>CISO Review</h3>
          <div className="badge-row">
            <span className={`badge ${verdictBadgeClass(reviewedTrack.ciso_verdict)}`}>
              {reviewedTrack.ciso_verdict}
            </span>
          </div>
          {reviewedTrack.ciso_summary && (
            <div className="one-line">{reviewedTrack.ciso_summary}</div>
          )}
          <RoundStatusLine
            roundNumber={reviewedTrack.ciso_round_number}
            stillOpenCount={reviewedTrack.ciso_still_open_count}
            resolutionKind={reviewedTrack.ciso_resolution_kind}
          />
        </div>
      )}
      {(reviewedTrack.rebuilt_from_track_id || (reviewedTrack.rebuild_generation ?? 0) > 0) && (
        <div className="badge-row">
          {reviewedTrack.rebuilt_from_track_id && (
            <a className="badge badge-gray" href={`/tracks/${reviewedTrack.rebuilt_from_track_id}`}>
              rebuilt from {reviewedTrack.rebuilt_from_track_id.slice(0, 8)}
            </a>
          )}
          {(reviewedTrack.rebuild_generation ?? 0) > 0 && (
            <span className="badge badge-gray">rebuild gen {reviewedTrack.rebuild_generation}</span>
          )}
        </div>
      )}
      <div className="kv" style={{ marginTop: 12 }}>
        <strong>id:</strong> <span className="code">{track.id}</span>
      </div>
      <EventTimeline trackId={track.id} />
    </div>
  );
}
